/**
 * パスワードバリデーション結果の型定義
 */
export interface PasswordValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * パスワード強度要件
 */
export interface PasswordRequirements {
  minLength?: number;
  requireNumber?: boolean;
  requireSpecialChar?: boolean;
  requireUpperCase?: boolean;
  requireLowerCase?: boolean;
}

/**
 * デフォルトのパスワード要件
 */
export const DEFAULT_PASSWORD_REQUIREMENTS: Required<PasswordRequirements> = {
  minLength: 8,
  requireNumber: true,
  requireSpecialChar: true,
  requireUpperCase: true,
  requireLowerCase: true,
};

/**
 * パスワードのバリデーションを実行する
 * @param password 検証するパスワード
 * @param requirements パスワード要件（オプション）
 * @returns バリデーション結果
 */
export const validatePassword = (
  password: string,
  requirements: PasswordRequirements = {}
): PasswordValidationResult => {
  const config = { ...DEFAULT_PASSWORD_REQUIREMENTS, ...requirements };

  if (password.length < config.minLength) {
    return {
      isValid: false,
      errorMessage: `パスワードは${config.minLength}文字以上である必要があります`,
    };
  }

  if (config.requireNumber && !/\d/.test(password)) {
    return {
      isValid: false,
      errorMessage: 'パスワードには少なくとも1つの数字を含める必要があります',
    };
  }

  if (config.requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return {
      isValid: false,
      errorMessage: 'パスワードには少なくとも1つの特殊文字を含める必要があります',
    };
  }

  if (config.requireUpperCase && !/[A-Z]/.test(password)) {
    return {
      isValid: false,
      errorMessage: 'パスワードには少なくとも1つの大文字を含める必要があります',
    };
  }

  if (config.requireLowerCase && !/[a-z]/.test(password)) {
    return {
      isValid: false,
      errorMessage: 'パスワードには少なくとも1つの小文字を含める必要があります',
    };
  }

  return {
    isValid: true,
    errorMessage: null,
  };
};

/**
 * パスワードとその確認が一致するかチェックする
 * @param password パスワード
 * @param confirmPassword 確認用パスワード
 * @returns 一致する場合はtrue、しない場合はfalse
 */
export const passwordsMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword;
};

/**
 * パスワード要件の説明文を取得する
 * @param requirements パスワード要件（オプション）
 * @returns 要件の説明文
 */
export const getPasswordRequirementText = (
  requirements: PasswordRequirements = {}
): string => {
  const config = { ...DEFAULT_PASSWORD_REQUIREMENTS, ...requirements };
  const parts: string[] = [];

  parts.push(`パスワードは${config.minLength}文字以上で`);

  const conditions: string[] = [];
  if (config.requireUpperCase) conditions.push('大文字');
  if (config.requireLowerCase) conditions.push('小文字');
  if (config.requireNumber) conditions.push('数字');
  if (config.requireSpecialChar) conditions.push('特殊文字');

  if (conditions.length > 0) {
    parts.push(`${conditions.join('・')}を含める必要があります`);
  }

  return parts.join('、');
};